import { lazy, Suspense, useId, type ComponentType, type ReactNode, type SVGProps } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { Link, useNavigate } from '@tanstack/react-router';
import { cn } from '@worldcoo/ui/lib/utils';
import { NativeSelect, NativeSelectOption } from '@worldcoo/ui/components/native-select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@worldcoo/ui/components/tabs';
import { Skeleton } from '@worldcoo/ui/components/skeleton';
import { DARK_FORM_FIELDS, DiamondGrid, diamondGlows, PillLink, pillSubmit } from '../primitives';
import { BuildingIcon, HeartIcon, MicrophoneIcon, PeopleIcon } from './icons';
import { Expand } from '../expand';
// A local placeholder here until TEC-3821; the footer and the FAQ need the same URL, so it lives in
// one module now — and so does the application form the NGO tab sends an entity to.
import { DONOR_APP_URL, NGO_APPLICATION_FORM_URL } from '../external-links';
import { DEFAULT_NGO_TAB, type Audience, type NgoTab } from './audiences';

// Lazy for the same reason the footer does it: the form pulls in zod, the form runtime and the terms
// dialog. Here it also means a visitor who has not yet said who they are downloads none of it.
const ContactForm = lazy(() => import('@/routes/-components/contact-form').then((m) => ({ default: m.ContactForm })));

/** The site's white pill, handed to the shared form: the form draws the button, the page styles it. */
type SubmitCta = { label: string; className: string; adornment: ReactNode };

/**
 * The hand-off draws this form at nearly half again the size the shared component ships at — 48px
 * controls against 32px, 16px text against 14px, 16px between fields against 24px. Applied from out
 * here, like the colours in DARK_FORM_FIELDS, so the one form serving `www` and Solutions is untouched.
 *
 * `[&_form]` reaches the <form> the shared component renders, which is where its own gap lives.
 */
const CONTACT_FORM_METRICS = [
	'[&_form]:gap-4',
	'[&_input]:h-12 [&_input]:px-4 [&_input]:text-base',
	'[&_textarea]:px-4 [&_textarea]:py-3 [&_textarea]:text-base',
	'[&_button[role=combobox]]:h-12 [&_button[role=combobox]]:px-4 [&_button[role=combobox]]:text-base',
	// The native select matches, and its chevron moves out with the padding it sits against.
	'[&_select]:h-12 [&_select]:pr-10 [&_select]:pl-4 [&_select]:text-base',
	'[&_[data-slot=native-select-icon]]:right-4',
].join(' ');

/**
 * Sized to the shortest form — name, email, message, consent, button — so picking an audience doesn't
 * make the card jump while the form chunk loads.
 */
function FormSkeleton() {
	return (
		<div className="flex flex-col gap-4">
			{[0, 1].map((i) => (
				<div key={i} className="flex flex-col gap-1">
					<Skeleton className="h-5 w-16 bg-white/10" />
					<Skeleton className="h-12 w-full bg-white/10" />
				</div>
			))}
			<div className="flex flex-col gap-1">
				<Skeleton className="h-5 w-32 bg-white/10" />
				<Skeleton className="h-24 w-full bg-white/10" />
			</div>
			<Skeleton className="h-5 w-64 bg-white/10" />
			<Skeleton className="h-11 w-40 rounded-full bg-white/10" />
		</div>
	);
}

/** The hand-off's confirmation: the panel drops the picker and the form and says only this. */
function SentMessage() {
	const { t } = useLingui();
	return (
		<div className="relative flex flex-1 flex-col justify-center gap-6 py-16 lg:max-w-[440px]">
			<h2 className="text-[32px] leading-[1.3] font-medium tracking-[-0.02em]">{t`Message sent successfully!`}</h2>
			<p className="leading-[1.5] text-white/70">
				{t`Thank you for getting in touch. We have received your message and will reply as soon as possible.`}
			</p>
		</div>
	);
}

/**
 * "Contacta con nosotros" — the dark panel that is the whole point of the Contact page.
 *
 * Two steps, as the hand-off draws them: the visitor first says who they are, on four full-width
 * pills, and only then is asked anything. Once picked, those pills collapse into a select, so the
 * answer stays changeable without four 80px buttons taking up half the card.
 *
 * What follows depends entirely on the audience, and each answer is the shortest one that works: a
 * company gets the full questionnaire, an NGO is pushed towards onboarding rather than a message, a
 * donor is first told they can answer their own question, and the press gets a box and nothing else.
 * A sent message replaces the lot with `SentMessage`.
 *
 * The picker NAVIGATES: each audience is its own route, so answering "I am…" is a page the visitor can
 * link to, bookmark and come back from with the browser's own button. `sent` is not state of its own
 * either — the left column's illustration animates when the message goes out and that column cannot
 * see this one, so the page owns it.
 */
export function ContactPanel({
	audience,
	tab,
	sent,
	onSent,
}: {
	audience: Audience | null;
	/** Which NGO tab the URL names. Ignored by every other audience, which has no tabs. */
	tab: NgoTab;
	sent: boolean;
	onSent: () => void;
}) {
	const { t } = useLingui();
	const navigate = useNavigate();
	const promptId = useId();

	const audiences: { value: Audience; label: string; icon: ComponentType<SVGProps<SVGSVGElement>> }[] = [
		{ value: 'company', label: t`Company`, icon: BuildingIcon },
		{ value: 'ngo', label: t`NGO / Social entity`, icon: HeartIcon },
		{ value: 'donor', label: t`Donor`, icon: PeopleIcon },
		{ value: 'press', label: t`Press and communication`, icon: MicrophoneIcon },
	];
	const selected = audiences.find(({ value }) => value === audience);
	const SelectedIcon = selected?.icon;

	// The site's white pill, scaled to this card the way every other control on it is: the hand-off
	// draws a 48px button with a 16px label against the 44px/14px one the rest of the site uses, which
	// is the same step up `CONTACT_FORM_METRICS` applies to the fields above it.
	const submit: SubmitCta = pillSubmit(t`Send message`, { className: 'h-12 gap-2.5 pr-3 pl-5 text-base' });

	return (
		// The right-hand column, so last in the sweep. See `Expand`.
		<Expand index={1.5} asChild>
			<section className="bg-ink relative flex flex-col overflow-hidden rounded-3xl px-6 pt-8 pb-8 text-white md:px-12 md:pt-10 md:pb-12 lg:min-h-[801px]">
				{/* One glow, centred at 81.5%/86.7% of the panel — measured off the hand-off's own ellipse. */}
				<DiamondGrid
					color="#fff"
					opacity={0.4}
					size={4.39}
					gap={27.59}
					masks={[diamondGlows({ x: '81.5%', y: '86.7%' })]}
				/>

				{sent ? (
					<SentMessage />
				) : (
					<>
						<h1 className="relative mb-6 text-[32px] leading-[1.3] font-medium tracking-[-0.02em]">
							<Trans>
								Contact <span className="text-olive">us</span>
							</Trans>
						</h1>

						<div className="relative flex flex-col gap-4">
							<p id={promptId} className="text-sm leading-[1.5] font-medium tracking-[-0.02em]">
								{t`Hi! I am…`}
							</p>

							{audience === null ? (
								<div role="group" aria-labelledby={promptId} className="flex flex-col gap-4">
									{audiences.map(({ value, label, icon: Icon }) => (
										<Link
											key={value}
											to="/www/contact/$audience"
											params={{ audience: value }}
											className="flex h-20 items-center justify-center gap-2 rounded-full border border-white px-4 py-3 font-medium transition-colors hover:bg-white/10"
										>
											<Icon className="size-4 shrink-0 opacity-50" />
											{label}
										</Link>
									))}
								</div>
							) : (
								/* A native `<select>`, so the open list is the platform's — its own wheel on a phone, its own
								   keyboard behaviour — for four labels that need no custom row. Only the CLOSED control is
								   ours, and it reproduces the combobox trigger it replaces exactly: 48px, `rounded-lg`, the
								   same `white/10` on `white/5`, 16px text.

								   The audience's icon is the one part a native list cannot carry, so it is laid OVER the
								   control (`pointer-events-none`, or it would swallow the click that opens it) and the
								   select's text is padded clear of it — 38px, which is the 16px inset plus the 16px glyph
								   plus the 6px gap the trigger had between the two. The options are drawn by the OS and
								   inherit the control's colour, so `text-ink` is what keeps them from being white on white,
								   exactly as DARK_FORM_FIELDS does it for the fields below. */
								<div className="relative">
									{SelectedIcon && (
										<SelectedIcon className="pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2 opacity-50" />
									)}
									<NativeSelect
										aria-labelledby={promptId}
										value={audience}
										// Looked up rather than cast: `event.target.value` is a plain string, and a cast would
										// let a value that is not an audience through as a route param that matches nothing.
										onChange={(event) => {
											const picked = audiences.find(({ value }) => value === event.target.value);
											if (picked) navigate({ to: '/www/contact/$audience', params: { audience: picked.value } });
										}}
										className={cn(
											'w-full',
											'[&_select]:h-12 [&_select]:rounded-lg [&_select]:border-white/10 [&_select]:bg-white/5',
											'[&_select]:pr-10 [&_select]:pl-[2.375rem] [&_select]:text-base [&_select]:text-white',
											'[&_option]:text-ink',
											'[&_[data-slot=native-select-icon]]:right-4 [&_[data-slot=native-select-icon]]:text-white/50',
										)}
									>
										{audiences.map(({ value, label }) => (
											<NativeSelectOption key={value} value={value}>
												{label}
											</NativeSelectOption>
										))}
									</NativeSelect>
								</div>
							)}
						</div>

						{/* NO Suspense boundary here. It used to wrap this whole block, which put the NGO tabs and
						    the donor's advice inside it: opening the enquiry tab mounts the lazy form for the first
						    time, the boundary fell back, and the control the visitor had just used to get there
						    vanished into the skeleton with it. Every boundary now sits BELOW its own selector,
						    around the form alone. */}
						<div className={cn('relative mt-4', DARK_FORM_FIELDS, CONTACT_FORM_METRICS)}>
							{audience === 'ngo' ? (
								<NgoBody tab={tab} submit={submit} onSuccess={onSent} />
							) : (
								audience !== null && (
									<>
										{audience === 'donor' && (
											<div className="mb-6 flex flex-col gap-2">
												<h2 className="leading-[1.4] font-medium">
													{t`Do you have questions about how to deduct your donation?`}
												</h2>
												<p className="max-w-[433px] text-sm leading-[1.5] text-white/50">
													<Trans>
														Go to{' '}
														<a
															href={DONOR_APP_URL}
															target="_blank"
															rel="noopener noreferrer"
															className="text-olive underline"
														>
															app.worldcoo.com
														</a>
														, open the “Add donation” section and register it with the donation code printed on your
														receipt, or by scanning the code directly. The donation is then certified automatically.
													</Trans>
												</p>
											</div>
										)}
										<Suspense fallback={<FormSkeleton />}>
											<ContactForm variant={audience} submit={submit} onSuccess={onSent} />
										</Suspense>
									</>
								)
							)}
						</div>
					</>
				)}
			</section>
		</Expand>
	);
}

/**
 * The NGO fork. An entity that wants to collaborate does not want to write a message — it wants the
 * onboarding form — but one that already collaborates may well have a question, so neither wins: the
 * two sit behind the hand-off's 53px tabs, with collaborating open first.
 *
 * The sign-up CTA opens the application form the NGO team reads, the same one the partner-NGOs page's
 * "Darse de alta" opens.
 */
function NgoBody({ tab, submit, onSuccess }: { tab: NgoTab; submit: SubmitCta; onSuccess: () => void }) {
	const { t } = useLingui();
	const navigate = useNavigate();

	const tabs: { value: NgoTab; label: string }[] = [
		{ value: 'collaborate', label: t`I want to collaborate` },
		{ value: 'enquire', label: t`I want to ask` },
	];

	// Controlled by the URL rather than by Radix's own state: the enquiry tab is a form, so it has an
	// address, and the two would drift the moment the visitor used the back button. The default tab has
	// no segment of its own, so it navigates back to the bare audience page.
	return (
		<Tabs
			value={tab}
			onValueChange={(value) => {
				const picked = tabs.find(({ value: tabValue }) => tabValue === value);
				if (!picked) return;
				if (picked.value === DEFAULT_NGO_TAB) navigate({ to: '/www/contact/$audience', params: { audience: 'ngo' } });
				else navigate({ to: '/www/contact/$audience/$tab', params: { audience: 'ngo', tab: picked.value } });
			}}
			className="gap-0"
		>
			{/* The list's height and the underline's offset are overridden on the same variant they are
			    set on, so tailwind-merge drops the defaults instead of leaving both in the stylesheet. */}
			<TabsList
				variant="line"
				className="w-full gap-0 border-b border-white/10 p-0 group-data-horizontal/tabs:h-[53px]"
			>
				{tabs.map((tab) => (
					<TabsTrigger
						key={tab.value}
						value={tab.value}
						className="rounded-none text-sm text-white/50 after:bg-white group-data-horizontal/tabs:after:bottom-0 hover:bg-white/5 hover:text-white data-active:bg-transparent data-active:text-white"
					>
						{tab.label}
					</TabsTrigger>
				))}
			</TabsList>

			<TabsContent value="collaborate" className="flex flex-col items-center gap-4 px-6 py-14 text-center">
				<h2 className="leading-[1.4] font-medium">{t`Do you want to start collaborating?`}</h2>
				<p className="max-w-[337px] text-sm leading-[1.5] text-white/50">
					{t`Fill in the form with your entity's basic information to start the sign-up process.`}
				</p>
				<PillLink variant="white" href={NGO_APPLICATION_FORM_URL}>
					{t`Go to the form`}
				</PillLink>
			</TabsContent>

			{/* The boundary is inside the tab, under the `TabsList`: the tabs are how you leave this
			    panel again, so they have to stay mounted and clickable while the form's chunk arrives. */}
			<TabsContent value="enquire" className="px-6 pt-8">
				<Suspense fallback={<FormSkeleton />}>
					<ContactForm variant="ngo" submit={submit} onSuccess={onSuccess} />
				</Suspense>
			</TabsContent>
		</Tabs>
	);
}
